import React, { useState, useEffect, useRef } from 'react';
import './TableStudents.css';
import StudentDetailModal from '../StudentDetailModal/StudentDetailModal';
import EditStudentModal from '../EditStudentModal/EditStudentModal';
import StudentHistoryModal from '../StudentHistoryModal/StudentHistoryModal';
import StudentNoteModal from '../StudentNoteModal/StudentNoteModal';
import SearchInput from '../../Search/Search';

/* --- Componentes de Ícone --- */
const ChevronDownIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="m6 9 6 6 6-6"></path>
    </svg>
);
const ChevronUpIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="m18 15-6-6-6 6" />
    </svg>
);
const DownloadIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
        <polyline points="7 10 12 15 17 10"></polyline>
        <line x1="12" x2="12" y1="15" y2="3"></line>
    </svg>
);
const EyeIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path>
        <circle cx="12" cy="12" r="3"></circle>
    </svg>
);
const EllipsisIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
        <circle cx="12" cy="12" r="1"></circle>
        <circle cx="19" cy="12" r="1"></circle>
        <circle cx="5" cy="12" r="1"></circle>
    </svg>
);

/* --- Constantes --- */
const cursoFilterOptions = ["Todos os cursos", "Engenharia de Software", "Ciência da Computação", "Sistemas de Informação"];
const statusFilterOptions = ["Todos os status", "Aprovado", "Reprovado", "Alerta"];
const assignableCourses = cursoFilterOptions.filter(course => course !== "Todos os cursos");

/* --- Funções Auxiliares de Status --- */
const getFrequenciaStatusClass = (frequenciaValue) => {
    if (!frequenciaValue || frequenciaValue === "N/A") return 'default';
    const numericValue = parseFloat(String(frequenciaValue).replace('%', ''));
    if (isNaN(numericValue)) return 'default';
    if (numericValue === 100) return 'perfect';
    if (numericValue >= 80) return 'positive';
    if (numericValue >= 75) return 'warning';
    return 'danger';
};

const getDesempenhoStatusClass = (desempenhoValue) => {
    if (!desempenhoValue || desempenhoValue === "N/A") return 'default';
    const numericValue = parseFloat(String(desempenhoValue));
    if (isNaN(numericValue)) return 'default';
    if (numericValue === 10) return 'perfect';
    if (numericValue >= 8) return 'positive';
    if (numericValue >= 7) return 'warning';
    return 'danger';
};

const getOverallStudentStatus = (student) => {
    const desempenhoClass = getDesempenhoStatusClass(student.desempenho);
    const frequenciaClass = getFrequenciaStatusClass(student.frequencia);

    if (desempenhoClass === 'danger' || frequenciaClass === 'danger') {
        return "Reprovado";
    }
    if (desempenhoClass === 'warning' || frequenciaClass === 'warning') {
        return "Alerta";
    }
    if ((desempenhoClass === 'perfect' || desempenhoClass === 'positive') &&
        (frequenciaClass === 'perfect' || frequenciaClass === 'positive')) {
        return "Aprovado";
    }
    return "Indefinido";
};

/* --- Componente Principal --- */
const TableStudents = ({ initialStudents, onUpdateStudents }) => {
    /* --- Estados e Refs --- */
    const [displayedStudentData, setDisplayedStudentData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    const [isCursoOpen, setIsCursoOpen] = useState(false);
    const [selectedCurso, setSelectedCurso] = useState("Todos os cursos");
    const cursoDropdownRef = useRef(null);

    const [isStatusOpen, setIsStatusOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState("Todos os status");
    const statusDropdownRef = useRef(null);

    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [isCurrentDropdownOpeningUp, setIsCurrentDropdownOpeningUp] = useState(false); // Para drop-up
    const actionsDropdownRef = useRef(null); // Para fechar ao clicar fora

    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedStudentForDetailModal, setSelectedStudentForDetailModal] = useState(null);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [studentToEdit, setStudentToEdit] = useState(null);

    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
    const [selectedStudentForHistory, setSelectedStudentForHistory] = useState(null);

    const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
    const [selectedStudentForNote, setSelectedStudentForNote] = useState(null);

    /* --- Efeitos --- */
    useEffect(() => {
        let filteredData = initialStudents || [];

        if (selectedCurso !== "Todos os cursos") {
            filteredData = filteredData.filter(student => student.curso === selectedCurso);
        }

        if (selectedStatus !== "Todos os status") {
            filteredData = filteredData.filter(student => {
                const overallStatus = getOverallStudentStatus(student);
                return overallStatus === selectedStatus;
            });
        }

        if (searchTerm) {
            filteredData = filteredData.filter(student =>
                (student.name && student.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (student.matricula && student.matricula.includes(searchTerm))
            );
        }

        const sortedAndFilteredData = [...filteredData].sort((a, b) => {
            if (a.name && b.name) {
                return a.name.localeCompare(b.name);
            }
            return 0;
        });

        setDisplayedStudentData(sortedAndFilteredData);
    }, [selectedCurso, selectedStatus, searchTerm, initialStudents]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (cursoDropdownRef.current && !cursoDropdownRef.current.contains(event.target)) {
                setIsCursoOpen(false);
            }
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
                setIsStatusOpen(false);
            }
            if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target)) {
                if (!event.target.closest('.stb-actions-group')) {
                    setOpenDropdownId(null);
                    setIsCurrentDropdownOpeningUp(false);
                }
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    /* --- Manipuladores (Handlers) --- */
    const handleCursoSelect = (curso) => {
        setSelectedCurso(curso);
        setIsCursoOpen(false);
    };

    const handleStatusSelect = (status) => {
        setSelectedStatus(status);
        setIsStatusOpen(false);
    };

    const handleToggleDropdown = (studentId, event) => {
        const isOpeningNewDropdown = openDropdownId !== studentId;
        if (isOpeningNewDropdown && event) {
            setOpenDropdownId(studentId);
            const buttonElement = event.currentTarget;
            const tableWrapper = buttonElement.closest('.stb-table-overflow-wrapper');

            if (buttonElement && tableWrapper) {
                const buttonRect = buttonElement.getBoundingClientRect();
                const tableWrapperRect = tableWrapper.getBoundingClientRect();

                const spaceBelowInTable = tableWrapperRect.bottom - buttonRect.bottom;
                const dropdownEstimatedHeight = 120;

                if (spaceBelowInTable < dropdownEstimatedHeight && (buttonRect.top - tableWrapperRect.top) > dropdownEstimatedHeight) {
                    setIsCurrentDropdownOpeningUp(true);
                } else {
                    setIsCurrentDropdownOpeningUp(false);
                }
            } else {
                setIsCurrentDropdownOpeningUp(false);
            }
        } else {
            setOpenDropdownId(openDropdownId === studentId ? null : studentId);
            if (openDropdownId === studentId) {
                setIsCurrentDropdownOpeningUp(false);
            }
        }
    };


    const handleOpenDetailModal = (student) => {
        setSelectedStudentForDetailModal(student);
        setIsDetailModalOpen(true);
        setOpenDropdownId(null);
        setIsCurrentDropdownOpeningUp(false);
    };
    const handleCloseDetailModal = () => {
        setIsDetailModalOpen(false);
        setSelectedStudentForDetailModal(null);
    };

    const handleOpenEditModal = (student) => {
        setStudentToEdit(student);
        setIsEditModalOpen(true);
        setOpenDropdownId(null);
        setIsCurrentDropdownOpeningUp(false);
    };
    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setStudentToEdit(null);
    };
    const handleSaveChanges = (updatedStudent) => {
        const updatedList = (initialStudents || []).map(s =>
            s.id === updatedStudent.id ? updatedStudent : s
        );
        if (onUpdateStudents) {
            onUpdateStudents(updatedList);
        }
        handleCloseEditModal();
    };

    const handleOpenHistoryModal = (student) => {
        setSelectedStudentForHistory(student);
        setIsHistoryModalOpen(true);
        setOpenDropdownId(null);
        setIsCurrentDropdownOpeningUp(false);
    };
    const handleCloseHistoryModal = () => {
        setIsHistoryModalOpen(false);
        setSelectedStudentForHistory(null);
    };

    const handleOpenNoteModal = (student) => {
        setSelectedStudentForNote(student);
        setIsNoteModalOpen(true);
        setOpenDropdownId(null);
        setIsCurrentDropdownOpeningUp(false);
    };
    const handleCloseNoteModal = () => {
        setIsNoteModalOpen(false);
        setSelectedStudentForNote(null);
    };
    const handleSaveStudentNote = (studentId, newNote) => {
        const updatedList = (initialStudents || []).map(s =>
            s.id === studentId ? { ...s, anotacoesPrivadas: newNote } : s
        );
        if (onUpdateStudents) {
            onUpdateStudents(updatedList);
        }
    };


    /* --- Renderização --- */
    return (
        <div className="stb-card" style={{ marginTop: '1rem' }}>
            <div className="stb-card-content">
                <div className="stb-controls-header">
                    <div className="stb-search-filters-group">
                        <div className="stb-search-input-wrapper">
                            <SearchInput
                                placeholder="Buscar por nome ou matrícula..."
                                onSearchChange={setSearchTerm}
                            />
                        </div>
                        <div className="stb-filter-buttons-group">
                            <div className="stb-dropdown-container" ref={cursoDropdownRef}>
                                <button type="button" className="stb-filter-select-button" onClick={() => setIsCursoOpen(!isCursoOpen)}>
                                    <span className="stb-filter-select-button-text">{selectedCurso}</span>
                                    {isCursoOpen ? <ChevronUpIcon className="stb-filter-select-button-icon" /> : <ChevronDownIcon className="stb-filter-select-button-icon" />}
                                </button>
                                {isCursoOpen && (
                                    <ul className="stb-dropdown-options-list">
                                        {cursoFilterOptions.map((curso) => (
                                            <li key={curso} className="stb-dropdown-option-item" onClick={() => handleCursoSelect(curso)}>
                                                {curso}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="stb-dropdown-container" ref={statusDropdownRef}>
                                <button type="button" className="stb-filter-select-button" onClick={() => setIsStatusOpen(!isStatusOpen)}>
                                    <span className="stb-filter-select-button-text">{selectedStatus}</span>
                                    {isStatusOpen ? <ChevronUpIcon className="stb-filter-select-button-icon" /> : <ChevronDownIcon className="stb-filter-select-button-icon" />}
                                </button>
                                {isStatusOpen && (
                                    <ul className="stb-dropdown-options-list">
                                        {statusFilterOptions.map((status) => (
                                            <li key={status} className="stb-dropdown-option-item" onClick={() => handleStatusSelect(status)}>
                                                {status}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="stb-header-actions" style={{ justifyContent: 'flex-end' }}>
                        <button className="stb-icon-button stb-export-button">
                            <DownloadIcon className="stb-icon-button-icon-base" />
                            <span className="stb-sr-only">Exportar</span>
                        </button>
                    </div>
                </div>

                <div className="stb-table-container-wrapper">
                    <div className="stb-table-overflow-wrapper">
                        <table className="stb-main-table">
                            <thead className="stb-table-header">
                                <tr className="stb-table-header-row">
                                    <th className="stb-th-aluno">Aluno</th>
                                    <th className="stb-th-matricula">Matrícula</th>
                                    <th className="stb-th-curso">Curso</th>
                                    <th className="stb-th-frequencia">Frequência</th>
                                    <th className="stb-th-desempenho">Desempenho</th>
                                    <th className="stb-th-acoes">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="stb-table-body">
                                {(displayedStudentData || []).map((student, index) => (
                                    <tr key={student.id || student.matricula} className="stb-table-data-row">
                                        <td className="stb-td-aluno">
                                            <div className="stb-aluno-info-container">
                                                <span className="stb-avatar-wrapper">
                                                    <img className="stb-avatar-img" alt={student.name} src={student.imgSrc} />
                                                </span>
                                                <div>
                                                    <div className="stb-aluno-name">{student.name}</div>
                                                    <div className="stb-aluno-email">{student.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="stb-td-matricula">{student.matricula}</td>
                                        <td className="stb-td-curso">
                                            <div className="stb-curso-info-container">
                                                <span>{student.curso}</span>
                                            </div>
                                        </td>
                                        <td className="stb-td-frequencia">
                                            <div className={`stb-status-badge stb-badge-${getFrequenciaStatusClass(student.frequencia)}`}>
                                                {student.frequencia}
                                            </div>
                                        </td>
                                        <td className="stb-td-desempenho">
                                            <div className={`stb-status-badge stb-badge-${getDesempenhoStatusClass(student.desempenho)}`}>
                                                {student.desempenho}
                                            </div>
                                        </td>
                                        <td className="stb-td-acoes">
                                            <div className="stb-actions-group">
                                                <button
                                                    className="stb-icon-button"
                                                    onClick={() => handleOpenDetailModal(student)}
                                                    aria-label={`Ver detalhes de ${student.name}`}
                                                >
                                                    <EyeIcon className="stb-icon-button-icon-base" />
                                                    <span className="stb-sr-only">Ver detalhes</span>
                                                </button>
                                                <button
                                                    className="stb-icon-button"
                                                    onClick={(e) => handleToggleDropdown(student.id || student.matricula, e)}
                                                    aria-label={`Mais opções para ${student.name}`}
                                                >
                                                    <EllipsisIcon className="stb-icon-button-icon-base" />
                                                    <span className="stb-sr-only">Mais opções</span>
                                                </button>
                                                {openDropdownId === (student.id || student.matricula) && (
                                                    <div
                                                        ref={actionsDropdownRef}
                                                        role="menu"
                                                        className={`stb-actions-dropdown ${isCurrentDropdownOpeningUp ? 'stb-actions-dropdown--open-up' : ''
                                                            }`}
                                                    >
                                                        <div
                                                            role="menuitem"
                                                            className="stb-actions-dropdown-item"
                                                            onClick={() => handleOpenEditModal(student)}
                                                            tabIndex={0}
                                                            onKeyDown={(e) => e.key === 'Enter' && handleOpenEditModal(student)}
                                                        >
                                                            Editar aluno
                                                        </div>
                                                        <div
                                                            role="menuitem"
                                                            className="stb-actions-dropdown-item"
                                                            onClick={() => handleOpenHistoryModal(student)}
                                                            tabIndex={0}
                                                            onKeyDown={(e) => e.key === 'Enter' && handleOpenHistoryModal(student)}
                                                        >
                                                            Ver histórico
                                                        </div>
                                                        <div
                                                            role="menuitem"
                                                            className="stb-actions-dropdown-item"
                                                            tabIndex={0}
                                                            onClick={() => handleOpenNoteModal(student)}
                                                            onKeyDown={(e) => e.key === 'Enter' && handleOpenNoteModal(student)}
                                                        >
                                                            Adicionar anotação
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <StudentDetailModal
                isOpen={isDetailModalOpen}
                onClose={handleCloseDetailModal}
                student={selectedStudentForDetailModal}
            />
            <EditStudentModal
                isOpen={isEditModalOpen}
                onClose={handleCloseEditModal}
                student={studentToEdit}
                onSave={handleSaveChanges}
                courseOptions={assignableCourses}
            />
            <StudentHistoryModal
                isOpen={isHistoryModalOpen}
                onClose={handleCloseHistoryModal}
                student={selectedStudentForHistory}
            />
            <StudentNoteModal
                isOpen={isNoteModalOpen}
                onClose={handleCloseNoteModal}
                student={selectedStudentForNote}
                onSaveNote={handleSaveStudentNote}
            />
        </div>
    );
};

export default TableStudents;